import { Outlet } from 'react-router'
import { SignedIn, UserButton, OrganizationSwitcher, useAuth, useUser } from '@clerk/clerk-react'
import React, { useState } from 'react'
import { cn } from '@/lib/utils'
import {
  CalendarClock,
  Rabbit,
  Phone,
  ClipboardPenLine,
  UserSearch,
  Workflow,
  Book,
  Bot,
  ChartBar,
  Target,
  Wrench,
  Mic,
  FlaskConical,
  PhoneCall,
  IdCard,
  ScrollText,
  ChevronDown,
  ChevronRight,
  MoreHorizontal,
  Stamp,
  History,
  FileText,
  Settings,
  Cog,
} from 'lucide-react'

import { Sidebar, SidebarBody, SidebarLink } from '@/components/sidebar'
import { BillingCard } from '@/components/BillingCard'
import { getBrandAssets } from '@/lib/branding'
import { useUserData } from '@/entities/user';

interface NavigationItem {
  kind: 'navigation'
  name: string
  href: string
  icon: React.ReactNode
  parent: string
  external?: boolean
  roles?: string[]
  exact?: boolean
  superadminOnly?: boolean
  hideForWhiteLabels?: string[]
}

interface SectionHeader {
  kind: 'section'
  name: string
}

const sidebarNavigation: (NavigationItem | SectionHeader)[] = [
  // 1. Monitoring Section
  {
    kind: 'section',
    name: 'Monitoring',
  },
  {
    kind: 'navigation',
    name: 'Daily Reports',
    href: '/dashboard',
    icon: <CalendarClock />,
    parent: 'Monitoring',
  },
  {
    kind: 'navigation',
    name: 'Real-time Data',
    href: '/realtime',
    icon: <Rabbit />,
    parent: 'Monitoring',
  },
  {
    kind: 'navigation',
    name: 'Conversations',
    href: '/history',
    icon: <Phone />,
    parent: 'Monitoring',
  },
  {
    kind: 'navigation',
    name: 'Custom Reports',
    href: '/reports',
    icon: <ChartBar />,
    parent: 'Monitoring',
  },
  {
    kind: 'navigation',
    name: 'Quality Assurance',
    href: '/qa',
    icon: <ClipboardPenLine />,
    parent: 'Monitoring',
  },

  // 2. AI Settings Section
  {
    kind: 'section',
    name: 'AI Settings',
  },
  {
    kind: 'navigation',
    name: 'Campaign Editor',
    href: '/campaigns',
    icon: <Target />,
    parent: 'AI Settings',
  },
  {
    kind: 'navigation',
    name: 'Agents',
    href: '/agents',
    icon: <Bot />,
    parent: 'AI Settings',
  },
  {
    kind: 'navigation',
    name: 'LLMs',
    href: '/scripts',
    icon: <Book />,
    parent: 'AI Settings',
  },
  {
    kind: 'navigation',
    name: 'Tools',
    href: '/tools',
    icon: <Wrench />,
    parent: 'AI Settings',
  },
  {
    kind: 'navigation',
    name: 'Voices',
    href: '/voices',
    icon: <Mic />,
    parent: 'AI Settings',
  },
  {
    kind: 'navigation',
    name: 'Simulations',
    href: '/simulations',
    icon: <FlaskConical />,
    parent: 'AI Settings',
  },

  // 3. Dialing Section
  {
    kind: 'section',
    name: 'Dialing',
  },
  {
    kind: 'navigation',
    name: 'Live Dialing',
    href: '/dialer',
    icon: <PhoneCall />,
    roles: ['admin', 'manager'],
    parent: 'Dialing',
    exact: true,
    hideForWhiteLabels: ['olos'],
  },
  {
    kind: 'navigation',
    name: 'Phone Numbers',
    href: '/dialer/callerids',
    icon: <IdCard />,
    roles: ['admin', 'manager'],
    parent: 'Dialing',
    hideForWhiteLabels: ['olos'],
  },
  {
    kind: 'navigation',
    name: 'Lists',
    href: '/lists',
    icon: <Workflow />,
    parent: 'Dialing',
  },
  {
    kind: 'navigation',
    name: 'Leads',
    href: '/lead_search',
    icon: <UserSearch />,
    parent: 'Dialing',
  },

  // 4. Admin Section (superadmin only)
  {
    kind: 'section',
    name: 'Admin',
  },
  {
    kind: 'navigation',
    name: 'Tool Types',
    href: '/tool-types-admin',
    icon: <Cog />,
    parent: 'Admin',
    superadminOnly: true,
  },
  {
    kind: 'navigation',
    name: 'Twilio Numbers',
    href: '/twilio-numbers',
    icon: <Phone />,
    parent: 'Admin',
    superadminOnly: true,
  },
  {
    kind: 'navigation',
    name: 'Simulation Settings',
    href: '/simulation-settings',
    icon: <Cog />,
    parent: 'Admin',
    superadminOnly: true,
  },
]

function StyledSidebar({ isOrgAdmin, isSuperadmin }: { isOrgAdmin?: boolean; isSuperadmin?: boolean }) {
  const [open, setOpen] = useState(false)
  const [moreMenuOpen, setMoreMenuOpen] = useState(false)
  const { isLoaded } = useAuth()
  const { user } = useUser()
  const sections = sidebarNavigation.filter(
    (item): item is SectionHeader => item.kind === 'section'
  )
  const whiteLabel = import.meta.env.VITE_PRODUCT_WHITE_LABEL
  const items = sidebarNavigation
    .filter((item): item is NavigationItem => item.kind === 'navigation')
    .filter((item) => !item.superadminOnly || isSuperadmin)
    .filter((item) => !item.hideForWhiteLabels?.includes(whiteLabel))

  const { logo, brandName } = getBrandAssets()

  return (
    <Sidebar open={open} setOpen={setOpen} animate={false}>
      <SidebarBody className="w-[240px] bg-white">
        <div className="flex flex-col flex-1 overflow-hidden">
          {/* Scrollable navigation area */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden">
            <div className="flex justify-center py-0 mb-4">
              <img src={logo} alt={`${brandName} Logo`} className="h-8 text-primary" />
            </div>

            <div className="flex flex-col gap-1">
              {sections.map((section) => {
                const sectionItems = items.filter(
                  (item) => item.parent === section.name
                )

                if (sectionItems.length === 0) return null

                return (
                  <div key={section.name} className="space-y-0.5">
                    {/* Section Header */}
                    <div className="px-3 py-1">
                      <h3 className="text-detail font-semibold text-gray-900 uppercase tracking-wider">
                        {section.name}
                      </h3>
                    </div>

                    {/* Section Items */}
                    <div className="space-y-0">
                      {sectionItems.map((item) => {
                        return (
                          <SidebarLink
                            key={item.name}
                            link={item}
                            className='rounded-lg ml-3 text-detail py-1'
                          />
                        )
                      })}
                    </div>

                    {/* Visual separator after each section */}
                    <div className="border-t border-gray-200 mx-3"></div>
                  </div>
                )
              })}

              {/* More Section - Collapsible submenu */}
              <div className="space-y-0.5">
                {/* More Header with arrow */}
                <button
                  onClick={() => setMoreMenuOpen(!moreMenuOpen)}
                  className="w-full px-3 py-1 flex items-center justify-between hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <MoreHorizontal className="h-4 w-4 text-gray-700" />
                    <h3 className="text-detail font-semibold text-gray-900 uppercase tracking-wider">
                      More
                    </h3>
                  </div>
                  {moreMenuOpen ? (
                    <ChevronDown className="h-4 w-4 text-gray-600" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-600" />
                  )}
                </button>

                {/* Submenu Items */}
                {moreMenuOpen && (
                  <div className="space-y-0">
                    <SidebarLink
                      link={{
                        name: 'API token',
                        href: '/api-token',
                        icon: <Stamp />,
                      }}
                      className='rounded-lg ml-3 text-detail py-1'
                    />
                    {whiteLabel !== 'olos' && (
                      <SidebarLink
                        link={{
                          name: 'Dialer Call Logs',
                          href: '/dialer/call-logs',
                          icon: <ScrollText />,
                        }}
                        className='rounded-lg ml-3 text-detail py-1'
                      />
                    )}
                    <SidebarLink
                      link={{
                        name: 'Change History',
                        href: '/change-history',
                        icon: <History />,
                      }}
                      className='rounded-lg ml-3 text-detail py-1'
                    />
                    <SidebarLink
                      link={{
                        name: 'Api Docs',
                        href: '/api-docs',
                        icon: <FileText />,
                      }}
                      className='rounded-lg ml-3 text-detail py-1'
                    />
                    <SidebarLink
                      link={{
                        name: 'Organization Settings',
                        href: '/organization-settings',
                        icon: <Settings />,
                      }}
                      className='rounded-lg ml-3 text-detail py-1'
                    />
                  </div>
                )}

                {/* Visual separator */}
                <div className="border-t border-gray-200 mx-3"></div>
              </div>

              {/* Balance Section */}
              <div className="space-y-0.5">
                <div className="px-3 py-1">
                  <h3 className="text-detail font-semibold text-gray-900 uppercase tracking-wider">
                    Balance
                  </h3>
                </div>
                <div className="px-2">
                  <BillingCard />
                </div>
              </div>
            </div>
          </div>

          {/* User Section - fixed at bottom */}
          {isLoaded && (
            <div className="flex-shrink-0 border-t border-gray-200 px-2 py-3 grid gap-2">
              <div className="relative">
                <UserButton
                  appearance={{
                    elements: {
                      rootBox: 'w-full',
                      userButtonTrigger:
                        'w-full p-1.5 hover:bg-gray-100 rounded-lg',
                      userButtonBox: 'w-full',
                      userButtonAvatarBox: 'w-5 h-5 rounded-md',
                      userButtonPopoverFooter: 'hidden',
                    },
                  }}
                />
                {user?.emailAddresses[0]?.emailAddress && (
                  <div className="absolute left-9 top-1/2 -translate-y-1/2 pointer-events-none">
                    <span className="text-body text-gray-700 truncate block max-w-[160px]">
                      {user.emailAddresses[0].emailAddress}
                    </span>
                  </div>
                )}
              </div>

              {isOrgAdmin && (
                <div className="w-full text-body">
                  <OrganizationSwitcher
                    hidePersonal={true}
                    afterCreateOrganizationUrl="/"
                    afterLeaveOrganizationUrl="/"
                    afterSelectOrganizationUrl="/"
                    appearance={{
                      elements: {
                        rootBox: 'w-full text-body',
                        organizationSwitcherTrigger:
                          'w-full p-1.5 hover:bg-gray-100 rounded-lg flex justify-between items-center text-body',
                        organizationSwitcherTriggerIcon: 'w-7 h-7',
                        organizationSwitcherPopoverFooter: 'hidden',
                      },
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </SidebarBody>
    </Sidebar>
  )
}

export function Layout() {
  // TODO: Get isOrgAdmin from user metadata or API
  const isOrgAdmin = true
  const { isSuperadmin } = useUserData();

  return (
    <SignedIn>
      <div
        className={cn(
          'rounded-md flex flex-col md:flex-row flex-1 border overflow-hidden',
          'h-screen w-screen max-w-[100vw] bg-gray-100'
        )}
      >
        <div className="bg-white border-r">
          <StyledSidebar isOrgAdmin={isOrgAdmin} isSuperadmin={isSuperadmin} />
        </div>
        <main
          className={cn(
            'flex-1 flex flex-col min-h-0',
            '[&_*:focus]:scroll-mt-[8rem] lg:[&_*:focus]:scroll-mt-[5rem]',
            'min-w-0', // crucial! min-w-0 to disable intrinsic minimum width constraints and prevent overflow
            'm-0', // symmetrical margin
            'text-black bg-gray-100' // color theme
          )}
        >
          <div className="flex-1 overflow-y-auto bg-gray-100 min-h-0">
            <Outlet />
          </div>
        </main>
      </div>
    </SignedIn>
  )
}